import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [vendor, setVendor] = useState(null);
  const [ready, setReady] = useState(false);

  const hydrate = useCallback(async () => {
    const token = localStorage.getItem("citydooh_token");
    if (!token) {
      setReady(true);
      return;
    }
    try {
      const r = await api.me();
      setVendor(r.data);
    } catch {
      localStorage.removeItem("citydooh_token");
      setVendor(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const login = useCallback(async (email, password) => {
    const r = await api.login({ email, password });
    localStorage.setItem("citydooh_token", r.data.token);
    setVendor(r.data.vendor);
    return r.data.vendor;
  }, []);

  const register = useCallback(async (payload) => {
    const r = await api.register(payload);
    localStorage.setItem("citydooh_token", r.data.token);
    setVendor(r.data.vendor);
    // Phase-21 — the backend may return a signup_coupon_outcome
    // (credited / rejected) alongside the vendor. Returning the
    // whole response so the caller can render a toast.
    return { vendor: r.data.vendor, signup_coupon_outcome: r.data.signup_coupon_outcome || null };
  }, []);

  // Guided-Setup signup path — identical side-effects to register() so
  // callers can pick either helper interchangeably. Body shape is
  // { registration: {...}, intake: {...} } — see VendorRegisterGuided
  // on the backend.
  const registerGuided = useCallback(async (payload) => {
    const r = await api.registerGuided(payload);
    localStorage.setItem("citydooh_token", r.data.token);
    setVendor(r.data.vendor);
    return r.data.vendor;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("citydooh_token");
    setVendor(null);
  }, []);

  // Memoise context value so consumers only re-render when the vendor
  // or readiness state actually changes (not on every provider render).
  const value = useMemo(
    () => ({ vendor, ready, login, register, registerGuided, logout, refresh: hydrate, setVendor }),
    [vendor, ready, login, register, registerGuided, logout, hydrate],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
